import { ReactElement, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';

import { Link, MenuItem, IconButton, Divider, Typography, Box } from '@mui/material';
import { Dropdown } from '@localstack/ui';
import {
  QuestionMark as QuestionMarkIcon,
  Download as DownloadIcon,
  MenuBook as DocsIcon,
  LocalLibraryRounded as CommunityIcon,
  MarkUnreadChatAlt as ContactIcon,
  CalendarToday as CalendarIcon,
  InsertInvitationRounded as InsertInvitationRoundedIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';

import { ExternalLink } from '~/constants';

import { AppRoute } from '~/config';

import { UpgradeFromTrialBanner } from '../UpgradeFromTrialBanner';
import { fetchRSS } from '../Blog/utils';

import { MainNavbar } from './MainNavbar';
import { SettingsNavbar } from './SettingsNavbar';
import { AdminNavbar } from './AdminNavbar';

const NavBarContainer = styled('div')(() => ({
  width: '280px',
  padding: '0.8rem',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
}));

const Footer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.disabled,
  marginTop: '0.5em',
  gap: theme.spacing(0.75),
}));

const HelpButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(0.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: '20px',
  border: `1px solid ${theme.palette.divider}`,
  minHeight: '32px',
  minWidth: '32px',
  '& .MuiSvgIcon-root': {
    fontSize: '0.875rem',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.grey[400],
  },
}));

const MenuLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: '8px',
  fontSize: '0.875rem',
  '&:focus, &:focus-visible': {
    outline: 'none',
    boxShadow: 'none',
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: `${theme.spacing(1)} 0`,
}));

const BlogPostContainer = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
}));

const BlogPostTitle = styled('div')(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  color: theme.palette.text.primary,
  lineHeight: 1.4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '240px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  padding: `${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  margin: 0,
}));

const StyledDropdown = styled(Dropdown)(({ theme }) => ({
  '& .MuiPaper-root': {
    minWidth: '320px',
    maxWidth: '320px',
    padding: '8px 0',
    boxShadow: theme.shadows[8],
    '& .MuiMenuItem-root': {
      fontSize: '0.875rem',
      padding: '1px 8px',
      minHeight: '20px',
      '&:focus, &:focus-visible': {
        outline: 'none',
        backgroundColor: 'transparent',
      },
    },
  },
}));

enum NavState {
  COMMON = 'common',
  SETTINGS = 'settings',
  ADMIN = 'admin',
}

/** Component that decides which version of the navbar to show to the user */
export const Navbar = (): ReactElement => {
  const location = useLocation();

  const { data, isLoading } = useSWR('rssFeed', fetchRSS);

  // Get the latest 2 blog posts
  const blogPosts = useMemo(() => {
    const posts = data?.rss.channel.item?.slice(0, 2) ?? [];
    return posts;
  }, [data]);

  const getNavState = () => {
    if (location.pathname.startsWith('/settings')) return NavState.SETTINGS;
    if (location.pathname.startsWith('/admin')) return NavState.ADMIN;
    return NavState.COMMON;
  };

  const navState = getNavState();

  return (
    <NavBarContainer data-name="navbar">
      {navState === NavState.COMMON && <MainNavbar />}
      {navState === NavState.SETTINGS && <SettingsNavbar />}
      {navState === NavState.ADMIN && <AdminNavbar />}
      <Footer>
        <StyledDropdown
          placement="top-start"
          renderButton={({ onClick }) => (
            <HelpButton
              onClick={onClick}
              aria-label="Help and resources menu"
              aria-haspopup="menu"
              id="help-menu-button"
            >
              <QuestionMarkIcon />
            </HelpButton>
          )}
          role="menu"
          aria-labelledby="help-menu-button"
        >
          <MenuItem role="menuitem">
            <MenuLink href={AppRoute.DESKTOP_DOWNLOAD} aria-label="Download LocalStack Desktop App">
              <DownloadIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} aria-hidden="true" />
              <span>Desktop App</span>
            </MenuLink>
          </MenuItem>

          <MenuItem role="menuitem">
            <MenuLink
              href={ExternalLink.DOCS_BASE}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Documentation (opens in new tab)"
            >
              <DocsIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} aria-hidden="true" />
              <span>Docs</span>
            </MenuLink>
          </MenuItem>

          <MenuItem role="menuitem">
            <MenuLink
              href="https://www.localstack.cloud/slack"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Join our Slack community (opens in new tab)"
            >
              <CommunityIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} aria-hidden="true" />
              <span>Community</span>
            </MenuLink>
          </MenuItem>

          <MenuItem role="menuitem">
            <MenuLink
              href={ExternalLink.CONTACT_TEAM}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contact us (opens in new tab)"
            >
              <ContactIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} aria-hidden="true" />
              <span>Contact us</span>
            </MenuLink>
          </MenuItem>

          <StyledDivider />

          <SectionTitle role="group" aria-label="Legal documents">
            Legal
          </SectionTitle>

          <MenuItem role="menuitem">
            <MenuLink
              href={ExternalLink.LEGAL_TOS}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Terms of Service (opens in new tab)"
            >
              <ArticleIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} aria-hidden="true" />
              <span>Terms of Service</span>
            </MenuLink>
          </MenuItem>

          <MenuItem role="menuitem">
            <MenuLink
              href={ExternalLink.LEGAL_PRIVACY_POLICY}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Privacy Policy (opens in new tab)"
            >
              <ArticleIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} aria-hidden="true" />
              <span>Privacy Policy</span>
            </MenuLink>
          </MenuItem>

          <MenuItem role="menuitem">
            <MenuLink
              href={ExternalLink.LEGAL_DPA}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Data Processing Addendum (opens in new tab)"
            >
              <ArticleIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} aria-hidden="true" />
              <span>Data Processing Addendum</span>
            </MenuLink>
          </MenuItem>

          <StyledDivider />

          <SectionTitle role="group" aria-label="Latest blog posts">
            What&apos;s new
          </SectionTitle>

          {isLoading ? (
            <MenuItem disabled role="menuitem" aria-label="Loading blog posts">
              <ArticleIcon sx={{ color: 'text.secondary' }} aria-hidden="true" />
              <span>Loading blog posts...</span>
            </MenuItem>
          ) : (
            blogPosts.map((post, index) => (
              <MenuItem key={post.link._text} role="menuitem">
                <MenuLink
                  href={post.link._text}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Read blog post: ${post.title._text} (opens in new tab)`}
                >
                  {index === 0 ? (
                    <InsertInvitationRoundedIcon
                      sx={{ fontSize: '1.25rem', color: 'text.secondary' }}
                      aria-hidden="true"
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '1.25rem',
                        height: '1.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      aria-hidden="true"
                    >
                      <FiberManualRecordIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} aria-hidden="true" />
                    </Box>
                  )}
                  <BlogPostContainer>
                    <BlogPostTitle>{post.title._text}</BlogPostTitle>
                  </BlogPostContainer>
                </MenuLink>
              </MenuItem>
            ))
          )}

          <MenuItem role="menuitem">
            <MenuLink
              href="https://blog.localstack.cloud"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View more blog posts (opens in new tab)"
            >
              <CalendarIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} aria-hidden="true" />
              <span>View more...</span>
            </MenuLink>
          </MenuItem>
        </StyledDropdown>
        <UpgradeFromTrialBanner />
      </Footer>
    </NavBarContainer>
  );
};
